import {
  Box,
  Flex,
  Form,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { selectPreferredRealEstateAgentMembership } from '../../../../../../../../store/selectors/clientTeamMemberships'
import { useDispatch, useSelector } from 'react-redux'
import Step from '../../../../../ModalStepsOutline'
import { If } from '../../../../../../../shared/Conditionals/Conditionals'
import { emailRegex } from '../../../../../../../../constants/regexes'
import { AppDispatch } from '../../../../../../../../store'
import { createTeamMembership } from '../../../../../../../../store/slices/homebotNetwork/clientTeamMemberships'
import { selectClientFetchId } from '../../../../../../../../store/selectors/client'
import * as Sentry from '@sentry/browser'
import { useEffect, useState } from 'react'
import {
  selectScheduleTourStep,
  setScheduleTourPreferredAgent,
  setScheduleTourShowModal,
  setScheduleTourStep,
  setScheduleTourStepAdvanceOneStep,
  STEPS,
  STEPS_CONST,
  STEPS_REA
} from '../../../../../../../../store/slices/scheduleTour'
import { Listing } from '../../../../../../../../api/gqlaxy/gql/generated/graphql'
import { selectCustomerIsRealEstateAgent } from '../../../../../../../../store/selectors/customerProfile'
import { useVerificationFlowEnabled } from '../../../../../../../../hooks'

export const MSG = defineMessages({
  preferredAgentRadioLabel: {
    id: 'ScheduleTour.AgentPreference.RadioLabel',
    defaultMessage: 'Agent preference'
  },
  listingAgent: {
    id: 'ScheduleTour.AgentPreference.YourListingAgent',
    defaultMessage: 'Listing agent'
  },
  yourPreferredAgent: {
    id: 'ScheduleTour.AgentPreference.YourPreferredAgent',
    defaultMessage: 'Your preferred agent'
  },
  addPreferredAgent: {
    id: 'ScheduleTour.AgentPreference.AddPreferredAgent',
    defaultMessage: 'Add preferred agent'
  },
  addPreferredAgentBody: {
    id: 'ScheduleTour.AgentPreference.AddPreferredAgentBody',
    defaultMessage: 'Send your questions to agent you know. We’ll save this info as an option for future questions.'
  },
  firstName: {
    id: 'TYV.valuation.cma.addPreferredAgent.form.firstName',
    defaultMessage: 'First name'
  },
  firstNameRequired: {
    id: 'TYV.valuation.cma.addPreferredAgent.form.firstNameRequired',
    defaultMessage: 'First name is required'
  },
  lastName: {
    id: 'TYV.valuation.cma.addPreferredAgent.form.lastName',
    defaultMessage: 'Last name'
  },
  lastNameRequired: {
    id: 'TYV.valuation.cma.addPreferredAgent.form.lastNameRequired',
    defaultMessage: 'Last name is required'
  },
  email: {
    id: 'TYV.valuation.cma.addPreferredAgent.form.email',
    defaultMessage: 'Email'
  },
  emailRequired: {
    id: 'TYV.valuation.cma.addPreferredAgent.form.emailRequired',
    defaultMessage: 'Email is required'
  },
  emailInvalid: {
    id: 'TYV.valuation.cma.addPreferredAgent.form.emailInvalid',
    defaultMessage: 'Invalid email address'
  },
  nextStep: {
    id: 'ScheduleTour.FooterButton.NextStep',
    defaultMessage: 'Next Step'
  },
  title: {
    id: 'ScheduleTour.AgentPreference.Label',
    defaultMessage: 'Who do you want to contact to help you get a tour scheduled?'
  }
})

export interface AgentPreferenceProps {
  listing: Listing
}

export const AgentPreference = ({ listing }: AgentPreferenceProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const intl = useIntl()
  const toast = useToast()

  const membership = useSelector(selectPreferredRealEstateAgentMembership)
  const preferredAgent = membership?.customerProfile
  const isVerificationFlowEnabled = useVerificationFlowEnabled()
  const listingAgent = listing?.agent?.name ?? listing?.agent?.email
  const listingAgentCompany = listing?.brokerage?.name || ''
  const clientId = useSelector(selectClientFetchId)
  const [isLoading, setIsLoading] = useState(false)
  const currentStep = useSelector(selectScheduleTourStep)
  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const stepIndex = STEPS.indexOf(currentStep)

  const shouldSkip = isVerificationFlowEnabled && preferredAgent
  // If the verification flow is enabled, and we have a preferred agent,
  // we can default the preference to preferredAgent and
  // go to the verification step
  useEffect(() => {
    if (shouldSkip) {
      dispatch(setScheduleTourPreferredAgent('preferredAgent'))
      dispatch(setScheduleTourStepAdvanceOneStep())
    }
  }, [isVerificationFlowEnabled, preferredAgent])

  const createMembership = async vals => {
    if (vals.preferredAgentSelection === 'preferredAgent') {
      setIsLoading(true)
      try {
        // if there is no preferred agent, create a new one with
        // the form values provided
        if (!preferredAgent) {
          await dispatch(
            createTeamMembership({
              clientId,
              preferredAgentFirstName: vals.firstName,
              preferredAgentLastName: vals.lastName,
              preferredAgentEmail: vals.email,
              isVerified: true,
              type: 'RealEstateAgent'
            })
          ).unwrap()
        }
      } catch (error) {
        console.error(error)
        Sentry.captureException(error)
        toast({
          title: 'Failed to add agent',
          status: 'error'
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleSubmit = async vals => {
    await createMembership(vals)
    dispatch(setScheduleTourPreferredAgent(vals.preferredAgentSelection))
    dispatch(setScheduleTourStepAdvanceOneStep())
  }

  if (shouldSkip) {
    return null
  }

  return (
    <Step stepLabel='schedule-tour-preferred-agent'>
      <Step.HeaderNavSection
        currentStep={currentStep}
        steps={isCustomerREA ? STEPS_REA : STEPS}
        onCancel={() => dispatch(setScheduleTourShowModal(false))}
        onBack={() => dispatch(setScheduleTourStep(STEPS_CONST[stepIndex - 1]))}
      />

      <Step.Title>{intl.formatMessage(MSG.title)}</Step.Title>
      <Step.Body>
        <Form
          // @ts-expect-error - there is an open request to allow this prop on the Form component
          noValidate
          autoComplete='off'
          onSubmit={handleSubmit}
          id='schedule-tour-preferred-agent'
          defaultValues={{
            preferredAgentSelection: preferredAgent ? 'preferredAgent' : 'listingAgent'
          }}
        >
          {({ formState: { errors }, watch }) => {
            const radioValue = watch('preferredAgentSelection')
            const shouldValidate = radioValue === 'preferredAgent'

            return (
              <Stack gap='4'>
                <FormControl size='sm'>
                  <FormLabel srOnly>{intl.formatMessage(MSG.preferredAgentRadioLabel)}</FormLabel>
                  <RadioGroup
                    name='preferredAgentSelection'
                    tracking={{
                      guid: 'kEarfY2HvzUqcnQE79LXc1',
                      ui_context: 'schedule_tour_agent_preference_step',
                      descriptor: MSG.preferredAgentRadioLabel.defaultMessage
                    }}
                  >
                    <Stack direction='column'>
                      <Radio value='listingAgent'>
                        <Heading size='3xs' variant='eyebrow' pb={0} my={0} color='primary' textAlign='left'>
                          {intl.formatMessage(MSG.listingAgent)}
                        </Heading>
                        <Text align={'left'} mb={0} size={'xs'}>
                          {listingAgent?.toUpperCase()}
                        </Text>
                        <Text align={'left'} size='3xs' color='neutral.700'>
                          {listingAgentCompany.toUpperCase()}
                        </Text>
                      </Radio>

                      <Radio value='preferredAgent'>
                        <Heading size='3xs' variant='eyebrow' pb={0} my={0} color={'primary'} textAlign={'left'}>
                          {intl.formatMessage(preferredAgent ? MSG.yourPreferredAgent : MSG.addPreferredAgent)}
                        </Heading>
                        {preferredAgent ? (
                          <Text
                            align={'left'}
                            size={'xs'}
                          >{`${preferredAgent?.firstName?.toUpperCase()} ${preferredAgent?.lastName?.toUpperCase()}`}</Text>
                        ) : (
                          <Text align='left' size='3xs' color='neutral.700'>
                            {intl.formatMessage(MSG.addPreferredAgentBody)}
                          </Text>
                        )}
                      </Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                <If condition={radioValue === 'preferredAgent' && !preferredAgent}>
                  <Stack gap='3'>
                    <Flex direction='row' gap='2'>
                      <FormControl isRequired={shouldValidate} isInvalid={!!errors?.firstName}>
                        <FormLabel>{intl.formatMessage(MSG.firstName)}</FormLabel>
                        <Input
                          data-1p-ignore='true'
                          name='firstName'
                          size='sm'
                          rules={
                            shouldValidate
                              ? {
                                  required: intl.formatMessage(MSG.firstNameRequired)
                                }
                              : undefined
                          }
                          tracking={{
                            guid: '3VAqseSzxNNKJxgzTJdi49',
                            ui_context: 'schedule_tour_agent_preference_step',
                            descriptor: MSG.firstName.defaultMessage
                          }}
                        />
                        <FormErrorMessage>{errors?.firstName?.message?.toString()}</FormErrorMessage>
                      </FormControl>
                      <FormControl size='sm' isRequired={shouldValidate} isInvalid={!!errors?.lastName}>
                        <FormLabel>{intl.formatMessage(MSG.lastName)}</FormLabel>
                        <Input
                          data-1p-ignore='true'
                          name='lastName'
                          size='sm'
                          rules={
                            shouldValidate
                              ? {
                                  required: intl.formatMessage(MSG.lastNameRequired)
                                }
                              : undefined
                          }
                          tracking={{
                            guid: 'wnqhfCV2AoNEMNLWYAaqKv',
                            ui_context: 'schedule_tour_agent_preference_step',
                            descriptor: MSG.lastName.defaultMessage
                          }}
                        />
                        <FormErrorMessage>{errors?.lastName?.message?.toString()}</FormErrorMessage>
                      </FormControl>
                    </Flex>
                    <FormControl size='sm' isRequired={shouldValidate} isInvalid={!!errors?.email}>
                      <FormLabel>{intl.formatMessage(MSG.email)}</FormLabel>
                      <Input
                        data-1p-ignore='true'
                        name='email'
                        type='email'
                        size='sm'
                        rules={
                          shouldValidate
                            ? {
                                required: intl.formatMessage(MSG.emailRequired),
                                pattern: {
                                  value: emailRegex,
                                  message: intl.formatMessage(MSG.emailInvalid)
                                }
                              }
                            : undefined
                        }
                        tracking={{
                          guid: 'fEmQj8mSYNfeQmJFa64P18',
                          ui_context: 'schedule_tour_agent_preference_step',
                          descriptor: MSG.email.defaultMessage
                        }}
                      />
                      <FormErrorMessage>{errors?.email?.message?.toString()}</FormErrorMessage>
                    </FormControl>
                  </Stack>
                </If>
              </Stack>
            )
          }}
        </Form>
      </Step.Body>
      <Step.FooterButton type='submit' form='schedule-tour-preferred-agent' loading={isLoading}>
        {intl.formatMessage(MSG.nextStep)}
      </Step.FooterButton>
    </Step>
  )
}
